import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getEncryptionKey, setEncryptionKey } from '../utils/EncryptionUtils';
import { Lock, X } from 'lucide-react';

import { useCompany } from '../context/CompanyContext';

import { useI18n } from '../hooks/useI18n';

const EncryptionKeyModal: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [key, setKey] = useState('');
    const [error, setError] = useState('');
    const { selectedCompany } = useCompany();
    const { isRTL } = useI18n();
    const { t } = useTranslation();

    useEffect(() => {
        const checkKey = () => {
            // Only check if a company is selected AND encryption is enabled for it
            if (selectedCompany.selectedCompany && selectedCompany.selectedCompany.encryptionFeatureEnabled) {
                const existingKey = getEncryptionKey();
                if (!existingKey) {
                    setIsOpen(true);
                } else {
                    setIsOpen(false); // Ensure it closes if key exists (e.g. entered in another tab)
                }
            } else {
                setIsOpen(false);
            }
        };

        checkKey();
        // Optional: Listen for storage events if we want to sync across tabs, 
        // but for now just checking on mount is sufficient for the requirement.
    }, [selectedCompany.selectedCompany]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!key.trim()) {
            setError(t('encryption.errors.empty'));
            return;
        }
        if (key.length < 8) {
            setError(t('encryption.errors.tooShort'));
            return;
        }

        setEncryptionKey(key);
        setIsOpen(false);
        setError('');
        // Reload to ensure services pick up the key if they cached anything (though our utils read from storage directly)
        // window.location.reload(); 
        // Actually, our utils read from localStorage every time, so no reload needed unless we want to re-fetch data.
        // Let's trigger a re-fetch or just let the user navigate. 
        // For now, just closing is enough as the user will likely navigate to a page.
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div
                dir={isRTL ? 'rtl' : 'ltr'}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 m-4 animate-in fade-in zoom-in duration-200 relative"
            >
                <button
                    onClick={() => setIsOpen(false)}
                    className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors`}
                    aria-label={t('encryption.modal.close')}
                >
                    <X size={20} />
                </button>
                <div className="flex flex-col items-center mb-6">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
                        <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
                        {t('encryption.modal.title')}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 text-center mt-2">
                        {t('encryption.modal.description')}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="encryptionKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('encryption.modal.label')}
                        </label>
                        <input
                            type="password"
                            id="encryptionKey"
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder={t('encryption.modal.placeholder')}
                            autoFocus
                        />
                        {error && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                                {error}
                            </p>
                        )}
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <p className="text-xs text-yellow-800 dark:text-yellow-200">
                            <strong>{t('encryption.modal.warningTitle')}</strong> {t('encryption.modal.warningText')}
                        </p>
                    </div>

                    <button
                        type="submit"
                        className="w-full py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                        {t('encryption.modal.submit')}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default EncryptionKeyModal;
